// Pure utility for matching uploaded creatives to selected placements
// based on the same compatibility/dimension rules the rest of the app
// already uses (see services/dcm/creatives.js:612-614).
//
// Only PRIMARY creatives match directly. BACKUP and COMPANION creatives
// ride along with their primary at submission time via creative.backupId
// / creative.companionId, mirroring the bundling in PreloaderStepper.js
// and services/dcm/creatives.js:314-321.

import {
  getMediaDurationSeconds,
  snapSecondsToDuration
} from "../components/AssetUploader/Shared/dimensionUtils";

export const CREATIVE_TYPE = {
  DISPLAY: "DISPLAY",
  INSTREAM_VIDEO: "INSTREAM_VIDEO",
  INSTREAM_AUDIO: "INSTREAM_AUDIO",
  // Site-served: the publisher serves the ad and CM360 only counts it, so
  // the creative is a tracking creative carrying no asset at all.
  TRACKING: "TRACKING"
};

export const COMPATIBILITY = {
  DISPLAY: "DISPLAY",
  DISPLAY_INTERSTITIAL: "DISPLAY_INTERSTITIAL",
  APP: "APP",
  APP_INTERSTITIAL: "APP_INTERSTITIAL",
  IN_STREAM_VIDEO: "IN_STREAM_VIDEO",
  IN_STREAM_AUDIO: "IN_STREAM_AUDIO"
};

// CM360 has four display compatibilities, not one. DISPLAY and
// DISPLAY_INTERSTITIAL cover desktop, mobile web and mobile apps for regular
// and interstitial ads; APP and APP_INTERSTITIAL are no longer allowed on new
// placements but still sit on older ones.
//
// Comparing against DISPLAY alone quietly excluded every interstitial
// placement from matching — which is where rich media interstitials run, and
// part of why Smart Traffic could not traffic rich media at all.
const DISPLAY_COMPATIBILITIES = new Set([
  COMPATIBILITY.DISPLAY,
  COMPATIBILITY.DISPLAY_INTERSTITIAL,
  COMPATIBILITY.APP,
  COMPATIBILITY.APP_INTERSTITIAL
]);

export function isDisplayCompatibility(compatibility) {
  return DISPLAY_COMPATIBILITIES.has(compatibility);
}

export function getCreativeType(creative) {
  if (creative && creative.file && creative.file.type) {
    const t = creative.file.type;
    if (t.startsWith("video/")) return CREATIVE_TYPE.INSTREAM_VIDEO;
    if (t.startsWith("audio/")) return CREATIVE_TYPE.INSTREAM_AUDIO;
    return CREATIVE_TYPE.DISPLAY;
  }

  // Fallback for existing DCM creatives (no file, but dcmCreative.type is set)
  if (creative && creative.dcmCreative && creative.dcmCreative.type) {
    const dt = creative.dcmCreative.type;
    // Only an existing GCM creative can be TRACKING — nothing uploaded ever
    // is, which is what keeps site-served existing-only for free.
    if (dt.includes("TRACKING")) return CREATIVE_TYPE.TRACKING;
    if (dt.includes("AUDIO")) return CREATIVE_TYPE.INSTREAM_AUDIO;
    if (dt.includes("VIDEO")) return CREATIVE_TYPE.INSTREAM_VIDEO;
    return CREATIVE_TYPE.DISPLAY;
  }

  return CREATIVE_TYPE.DISPLAY;
}

export function getCompatibilityForCreativeType(type) {
  if (type === CREATIVE_TYPE.INSTREAM_VIDEO) return COMPATIBILITY.IN_STREAM_VIDEO;
  if (type === CREATIVE_TYPE.INSTREAM_AUDIO) return COMPATIBILITY.IN_STREAM_AUDIO;
  return COMPATIBILITY.DISPLAY;
}

export function getCreativeTypeFromPlacement(placement, siteServedIds) {
  if (!placement) return CREATIVE_TYPE.DISPLAY;
  // Site-served wins over compatibility: such a placement is usually typed
  // DISPLAY in CM360, but nothing display can run on it.
  if (siteServedIds && toIdSet(siteServedIds).has(String(placement.id))) {
    return CREATIVE_TYPE.TRACKING;
  }
  if (placement.compatibility === COMPATIBILITY.IN_STREAM_VIDEO) return CREATIVE_TYPE.INSTREAM_VIDEO;
  if (placement.compatibility === COMPATIBILITY.IN_STREAM_AUDIO) return CREATIVE_TYPE.INSTREAM_AUDIO;
  return CREATIVE_TYPE.DISPLAY;
}

/**
 * Normalize a set/array of placement ids to strings.
 *
 * Placement_Id arrives from SQL as a bigint while CM360 ids are strings, and
 * a Set does not coerce — so an unnormalized set silently matches nothing.
 */
export function toIdSet(value) {
  if (value instanceof Set) {
    const out = new Set();
    for (const v of value) out.add(String(v));
    return out;
  }
  return new Set((value || []).map((v) => String(v)));
}

/**
 * Site-served placement ids, from the placementInfo slice.
 *
 * @param {object} placementInfoById - state.placementInfo.byId
 */
export function buildSiteServedIds(placementInfoById) {
  const ids = new Set();
  for (const info of Object.values(placementInfoById || {})) {
    if (info && info.Serving_Type === "Site" && info.Placement_Id != null) {
      ids.add(String(info.Placement_Id));
    }
  }
  return ids;
}

// taxonomy.dimensions format: "300x250" (no spaces, retina-halved if applicable)
// creative.dimensions format:  "300 x 250" (with spaces, raw image size)
function parseDimensionString(s) {
  if (typeof s !== "string") return null;
  const parts = s.split("x").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const w = parseInt(parts[0], 10);
  const h = parseInt(parts[1], 10);
  if (Number.isNaN(w) || Number.isNaN(h)) return null;
  return { width: w, height: h };
}

// Media length as stored in gcm.Placement.Media_Length and in a creative's
// taxonomy. The column is not clean: ":30" and "30" both occur, "NA" is the
// taxonomy's explicit not-applicable, and a couple percent of rows hold junk
// the ETL dropped in the slot ("In-Stream Video"). Everything that isn't a
// plain count of seconds means "no usable length".
//
// Compared as a number, never a string — the configured list zero-pads (":06")
// while the column often doesn't ("6"), and those must not read as different
// spot lengths.
const MEDIA_LENGTH_PATTERN = /^:?(\d{1,3})$/;

export function parseMediaLengthSeconds(value) {
  if (typeof value !== "string") return null;
  const match = MEDIA_LENGTH_PATTERN.exec(value.trim());
  if (!match) return null;
  const seconds = parseInt(match[1], 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

export function formatMediaLength(seconds) {
  return ":" + (seconds < 10 ? "0" + seconds : String(seconds));
}

/**
 * Human label for an ad group, e.g. "In-Stream Video :30", "Display 300 x 250".
 *
 * The spot length has to be in here. Video and audio placements are all sized
 * 0x0, so once ads are keyed on length a :15 ad and a :30 ad would otherwise
 * both read "In-Stream Video" — identical titles on two different ads, in
 * both the step list and the review.
 */
export function getAdGroupLabel(group) {
  if (!group) return "";

  const suffix =
    group.durationSeconds === undefined || group.durationSeconds === null
      ? ""
      : ` ${formatMediaLength(group.durationSeconds)}`;

  if (group.type === CREATIVE_TYPE.INSTREAM_VIDEO) {
    return `In-Stream Video${suffix}`;
  }
  if (group.type === CREATIVE_TYPE.INSTREAM_AUDIO) {
    return `In-Stream Audio${suffix}`;
  }
  if (group.type === CREATIVE_TYPE.TRACKING) {
    return `Site-Served ${group.size.width} x ${group.size.height}`;
  }
  return `Display ${group.size.width} x ${group.size.height}`;
}

/**
 * A creative's spot length in seconds, or null if it has none.
 *
 * @param {object} creative
 * @param {string[]} [durationOptions] - the configured `:NN` list, used to snap
 *   CM360's detected duration the same way the naming step's group headers do,
 *   so a card can never sit in a group its placement match disagrees with.
 */
export function getCreativeDurationSeconds(creative, durationOptions) {
  if (!creative) return null;

  // Taxonomy and probe values are already snapped to the configured list.
  const fromTaxonomy = parseMediaLengthSeconds(
    creative.taxonomy && creative.taxonomy.dimensions
  );
  if (fromTaxonomy !== null) return fromTaxonomy;

  const fromProbe = parseMediaLengthSeconds(creative.dimensions);
  if (fromProbe !== null) return fromProbe;

  const detected = getMediaDurationSeconds(creative);
  if (detected !== null) {
    const snapped = snapSecondsToDuration(detected, durationOptions);
    const parsed = parseMediaLengthSeconds(snapped);
    return parsed !== null ? parsed : Math.round(detected);
  }

  return null;
}

/**
 * Build the placement-id → spot-length map from the placementInfo slice.
 *
 * CM360 has no usable duration of its own here: Placement.videoSettings
 * declares a durationSeconds, but it is populated on none of the account's
 * video placements. Media_Length, parsed by the ETL from the placement
 * taxonomy, resolves about three quarters of them — and roughly twice as
 * many as parsing the placement name directly would.
 *
 * @param {object} placementInfoById - state.placementInfo.byId
 * @returns {Map<string, number>} keyed by placement id, seconds as the value
 */
export function buildPlacementDurations(placementInfoById) {
  const durations = new Map();

  for (const info of Object.values(placementInfoById || {})) {
    if (!info || info.Placement_Id === undefined || info.Placement_Id === null) {
      continue;
    }
    const seconds = parseMediaLengthSeconds(info.Media_Length);
    if (seconds !== null) durations.set(String(info.Placement_Id), seconds);
  }

  return durations;
}

/**
 * Whether a media creative may go on a media placement.
 *
 * Exact spot length when both sides declare one: a :15 does not belong on a
 * :30 buy. Either side missing stays permissive rather than stranding the
 * creative — about a quarter of video placements have no usable Media_Length,
 * and before this they matched everything, so silence has to keep meaning
 * "no opinion" rather than "no".
 */
function durationsAgree(placement, creativeSeconds, placementDurations) {
  const placementSeconds = placementDurations.get(String(placement.id));
  if (placementSeconds === undefined) return true;
  if (creativeSeconds === null) return true;
  return placementSeconds === creativeSeconds;
}

function toDurationMap(value) {
  if (value instanceof Map) return value;
  const map = new Map();
  for (const [k, v] of Object.entries(value || {})) map.set(String(k), v);
  return map;
}

export function getCreativeDimensions(creative) {
  if (!creative) return null;

  // Prefer taxonomy.dimensions because it is already retina-halved
  // (CreativeNameManager.js:261-263) and matches what the API will send.
  if (creative.taxonomy && creative.taxonomy.dimensions) {
    const parsed = parseDimensionString(creative.taxonomy.dimensions);
    if (parsed) return parsed;
  }

  if (creative.dimensions && creative.dimensions !== "N/A") {
    const parsed = parseDimensionString(creative.dimensions);
    if (parsed) return parsed;
  }

  // Existing DCM creatives store size on dcmCreative.size
  if (creative.dcmCreative && creative.dcmCreative.size) {
    const sz = creative.dcmCreative.size;
    if (typeof sz.width === "number" && typeof sz.height === "number") {
      return { width: sz.width, height: sz.height };
    }
  }

  return null;
}

function dimsEqual(a, b) {
  return a && b && a.width === b.width && a.height === b.height;
}

export function reasonForUnmatchedCreative(creative, placements, options) {
  const opts = options || {};
  const ctype = getCreativeType(creative);

  if (ctype === CREATIVE_TYPE.TRACKING) {
    return "No site-served placement selected";
  }
  if (
    ctype === CREATIVE_TYPE.INSTREAM_VIDEO ||
    ctype === CREATIVE_TYPE.INSTREAM_AUDIO
  ) {
    const label =
      ctype === CREATIVE_TYPE.INSTREAM_VIDEO
        ? "IN_STREAM_VIDEO"
        : "IN_STREAM_AUDIO";
    const seconds = getCreativeDurationSeconds(creative, opts.durationOptions);
    // Naming the length is the difference between "nothing matched" and
    // "you selected video placements, just not any :30 ones".
    if (seconds !== null) {
      const compat =
        ctype === CREATIVE_TYPE.INSTREAM_VIDEO
          ? COMPATIBILITY.IN_STREAM_VIDEO
          : COMPATIBILITY.IN_STREAM_AUDIO;
      const hasCompatible = (placements || []).some(
        (p) => p.compatibility === compat
      );
      if (hasCompatible) {
        return `No ${formatMediaLength(seconds)} ${label} placement selected`;
      }
    }
    return `No ${label} placement selected`;
  }
  // DISPLAY
  const dims = getCreativeDimensions(creative);
  const hasDisplayPlacement = placements.some(
    (p) => isDisplayCompatibility(p.compatibility)
  );
  if (!hasDisplayPlacement) return "No DISPLAY placement selected";
  if (!dims) return "Creative has no usable dimensions to match a DISPLAY placement";
  return `No ${dims.width}x${dims.height} DISPLAY placement selected`;
}

export function reasonForUnmatchedPlacement(placement, options) {
  const opts = options || {};

  // Site-served takes nothing but a tracking creative, and those are
  // existing-only — there is nothing to upload for them.
  if (toIdSet(opts.siteServedIds).has(String(placement.id))) {
    return "No site-served tracking creative selected";
  }

  if (
    placement.compatibility === COMPATIBILITY.IN_STREAM_VIDEO ||
    placement.compatibility === COMPATIBILITY.IN_STREAM_AUDIO
  ) {
    const medium =
      placement.compatibility === COMPATIBILITY.IN_STREAM_VIDEO
        ? "video"
        : "audio";
    const seconds = toDurationMap(opts.placementDurations).get(
      String(placement.id)
    );
    if (seconds !== undefined) {
      return `No ${formatMediaLength(seconds)} ${medium} creative uploaded`;
    }
    return `No ${medium} creative uploaded`;
  }
  const w = placement.size && placement.size.width;
  const h = placement.size && placement.size.height;
  return `No ${w}x${h} display creative uploaded`;
}

/**
 * Auto-match creatives to placements.
 *
 * @param {Array} creatives  - all creatives (PRIMARY + BACKUP + COMPANION). Only PRIMARY are matched.
 * @param {Array} placements - selected placements.
 * @param {Object} [options]
 * @param {Set<string>|Array<string>} [options.siteServedIds] - placement IDs to skip with a Site-Served reason.
 * @param {Map<string,number>|Object} [options.placementDurations] - placement ID -> spot length
 *   in seconds, from buildPlacementDurations. Placements absent from the map have no usable
 *   Media_Length and stay permissive.
 * @param {string[]} [options.durationOptions] - the configured `:NN` list, for snapping a
 *   creative's CM360-detected duration.
 * @returns {{
 *   matches: { [placementId]: string[] },
 *   unmatchedCreatives: Array<{ id: string, reason: string }>,
 *   unmatchedPlacements: Array<{ id: string, reason: string }>
 * }}
 */
export function matchCreativesToPlacements(creatives, placements, options) {
  const opts = options || {};
  const siteServedIds = toIdSet(opts.siteServedIds);

  const placementDurations = toDurationMap(opts.placementDurations);
  const durationOptions = opts.durationOptions;

  const safeCreatives = Array.isArray(creatives) ? creatives : [];
  const safePlacements = Array.isArray(placements) ? placements : [];

  const matches = {};
  const unmatchedPlacements = [];
  const matchablePlacements = [];

  for (const p of safePlacements) {
    if (!p || !p.id) continue;
    matches[p.id] = [];

    matchablePlacements.push(p);
  }

  const primaries = safeCreatives.filter(
    (c) => c && (c.creativeRole || "PRIMARY") === "PRIMARY"
  );

  const unmatchedCreatives = [];

  for (const creative of primaries) {
    const ctype = getCreativeType(creative);
    const cdims = getCreativeDimensions(creative);
    const cseconds = getCreativeDurationSeconds(creative, durationOptions);
    let matchedThisCreative = false;

    for (const placement of matchablePlacements) {
      const isSiteServed = siteServedIds.has(String(placement.id));

      if (ctype === CREATIVE_TYPE.TRACKING) {
        // Tracking creatives go on site-served placements and nowhere else,
        // and never match on size: every tracking creative is 0x0 while the
        // placements they run on are 1x1 or a real banner size.
        if (!isSiteServed) continue;
      } else if (isSiteServed) {
        // Nothing else belongs on a site-served placement — the publisher
        // serves the actual ad, so only a tracking creative makes sense.
        continue;
      } else if (ctype === CREATIVE_TYPE.DISPLAY) {
        if (!isDisplayCompatibility(placement.compatibility)) continue;
        if (!cdims) continue;
        if (!placement.size) continue;
        if (!dimsEqual(cdims, placement.size)) continue;
      } else if (ctype === CREATIVE_TYPE.INSTREAM_VIDEO) {
        if (placement.compatibility !== COMPATIBILITY.IN_STREAM_VIDEO) continue;
        if (!durationsAgree(placement, cseconds, placementDurations)) continue;
      } else if (ctype === CREATIVE_TYPE.INSTREAM_AUDIO) {
        if (placement.compatibility !== COMPATIBILITY.IN_STREAM_AUDIO) continue;
        if (!durationsAgree(placement, cseconds, placementDurations)) continue;
      }

      matches[placement.id].push(creative.id);
      matchedThisCreative = true;
    }

    if (!matchedThisCreative) {
      unmatchedCreatives.push({
        id: creative.id,
        reason: reasonForUnmatchedCreative(creative, matchablePlacements, {
          durationOptions
        })
      });
    }
  }

  for (const placement of matchablePlacements) {
    if (matches[placement.id].length === 0) {
      unmatchedPlacements.push({
        id: placement.id,
        reason: reasonForUnmatchedPlacement(placement, {
          placementDurations,
          siteServedIds
        })
      });
    }
  }

  return {
    matches,
    unmatchedCreatives,
    unmatchedPlacements
  };
}

/**
 * Apply user overrides on top of an auto-match result.
 *
 * @param {Object} autoMatches - { [placementId]: creativeId[] } from matchCreativesToPlacements.
 * @param {Object} overrides   - { added: { [pid]: Set<cid> }, removed: { [pid]: Set<cid> } }.
 * @returns {Object} final assignment { [placementId]: creativeId[] } with overrides applied.
 */
export function applyOverrides(autoMatches, overrides) {
  const safeAuto = autoMatches || {};
  const added = (overrides && overrides.added) || {};
  const removed = (overrides && overrides.removed) || {};

  const placementIds = new Set([
    ...Object.keys(safeAuto),
    ...Object.keys(added),
    ...Object.keys(removed)
  ]);

  const out = {};
  for (const pid of placementIds) {
    const auto = safeAuto[pid] || [];
    const rm = removed[pid] || new Set();
    const ad = added[pid] || new Set();

    const seen = new Set();
    const result = [];
    for (const cid of auto) {
      if (rm.has && rm.has(cid)) continue;
      if (Array.isArray(rm) && rm.includes(cid)) continue;
      if (seen.has(cid)) continue;
      seen.add(cid);
      result.push(cid);
    }
    const adIter = ad.forEach ? ad : (Array.isArray(ad) ? ad : []);
    if (adIter && adIter.forEach) {
      adIter.forEach((cid) => {
        if (seen.has(cid)) return;
        seen.add(cid);
        result.push(cid);
      });
    } else if (Array.isArray(adIter)) {
      for (const cid of adIter) {
        if (seen.has(cid)) continue;
        seen.add(cid);
        result.push(cid);
      }
    }
    out[pid] = result;
  }
  return out;
}

/**
 * Group a final assignment map by (type, width, height) for batched
 * insertCreatives() calls. The backend API expects one compatibility/size
 * group per call (services/dcm/creatives.js).
 *
 * Each group's creativeIds order is the submission order, which BartAPI turns
 * into the CM360 `sequence` for SEQUENTIAL rotation (buildCreativeAssignments
 * in services/dcm/ads.ts uses `sequence: index + 1`). Pass orderedCreativeIds
 * — normally state.creatives.allIds, the order the drag-and-drop asset list
 * renders — to make that sequence follow what the user arranged. Without it
 * the order falls out of placement/match iteration order, which no UI controls.
 *
 * @param {Object} finalAssignments - { [placementId]: creativeId[] } from applyOverrides.
 * @param {Array} placements        - placements (used for compatibility + size lookup).
 * @param {string[]} [orderedCreativeIds] - authoritative creative ordering; ids
 *   missing from it sort last, keeping their relative order.
 * @param {Object} [options]
 * @param {Map<string,number>|Object} [options.placementDurations] - from buildPlacementDurations.
 * @param {Object} [options.adOverrides] - { [placementId]: slotNumber }, the manual split.
 * @returns {Array<{ key: string, naturalKey: string, slot: number, type: string, size: {width, height}, placements: Array, creativeIds: string[] }>}
 */

/**
 * The ad a placement falls into with no manual split applied.
 *
 * Spot length is part of an ad's identity, not just of matching. Video and
 * audio placements are all sized 0x0, so keying on type and size alone puts a
 * :30 and a :15 buy in the same ad — and an ad serves every one of its
 * creatives to every one of its placements, which undoes the duration
 * matching completely.
 */
export function getNaturalAdKey(placement, placementDurations, siteServedIds) {
  const durations = toDurationMap(placementDurations);
  const type = getCreativeTypeFromPlacement(placement, siteServedIds);
  const w = (placement.size && placement.size.width) || 0;
  const h = (placement.size && placement.size.height) || 0;
  const seconds = durations.get(String(placement.id));
  const duration = seconds === undefined ? "" : String(seconds);
  return `${type}|${w}x${h}|${duration}`;
}

export function groupAssignmentsForSubmission(
  finalAssignments,
  placements,
  orderedCreativeIds,
  options
) {
  const opts = options || {};
  const placementDurations = toDurationMap(opts.placementDurations);
  const siteServedIds = toIdSet(opts.siteServedIds);
  const adOverrides = opts.adOverrides || {};

  const safeFinal = finalAssignments || {};
  const placementById = new Map();
  for (const p of placements || []) {
    if (p && p.id) placementById.set(p.id, p);
  }

  const groups = new Map();

  for (const pid of Object.keys(safeFinal)) {
    const cids = safeFinal[pid];
    if (!cids || cids.length === 0) continue;
    const placement = placementById.get(pid);
    if (!placement) continue;

    const type = getCreativeTypeFromPlacement(placement, siteServedIds);
    const w = (placement.size && placement.size.width) || 0;
    const h = (placement.size && placement.size.height) || 0;
    // Size stays in the key for site-served too: insertCreatives sends the
    // first placement's size for the whole group, so a 1x1 and a 300x250
    // site-served placement must not share an ad.
    const naturalKey = getNaturalAdKey(
      placement,
      placementDurations,
      siteServedIds
    );
    // Slots split one natural group into several ads. Scoped to the natural
    // key rather than global, because CM360 ads are per type and size — two
    // placements that disagree on those can never share an ad however the
    // user arranges them. Always suffixed, so an explicit slot 1 and an
    // untouched placement land in the same ad rather than two.
    const slot = Number(adOverrides[pid]) || 1;
    const key = `${naturalKey}#${slot}`;

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        naturalKey,
        slot,
        type,
        size: { width: w, height: h },
        durationSeconds: placementDurations.get(String(placement.id)),
        placements: [],
        creativeIds: new Set()
      };
      groups.set(key, group);
    }
    group.placements.push(placement);
    for (const cid of cids) group.creativeIds.add(cid);
  }

  const orderIndex = new Map();
  if (Array.isArray(orderedCreativeIds)) {
    orderedCreativeIds.forEach((id, i) => orderIndex.set(id, i));
  }

  const sortByGivenOrder = (cids) => {
    if (orderIndex.size === 0) return cids;
    // Decorate with the current position so ids absent from
    // orderedCreativeIds keep their relative order instead of being
    // shuffled by an unstable comparator.
    return cids
      .map((id, i) => ({ id, i }))
      .sort((a, b) => {
        const ai = orderIndex.has(a.id)
          ? orderIndex.get(a.id)
          : Number.MAX_SAFE_INTEGER;
        const bi = orderIndex.has(b.id)
          ? orderIndex.get(b.id)
          : Number.MAX_SAFE_INTEGER;
        if (ai !== bi) return ai - bi;
        return a.i - b.i;
      })
      .map((entry) => entry.id);
  };

  return Array.from(groups.values()).map((g) => ({
    key: g.key,
    naturalKey: g.naturalKey,
    slot: g.slot,
    type: g.type,
    size: g.size,
    durationSeconds: g.durationSeconds,
    placements: g.placements,
    creativeIds: sortByGivenOrder(Array.from(g.creativeIds))
  }));
}

/**
 * The ad choices to offer for one placement in the Match Preview.
 *
 * Only ads sharing the placement's natural key are offered — anything else
 * would be a move CM360 cannot express. `adNumber` is the position in the
 * full group list, so the label matches the per-ad options steps the user
 * will walk through next.
 *
 * @returns {{ slot: number, adNumber: number }[]} existing ads, in step order
 */
export function getAdSlotChoices(placement, groups, placementDurations, siteServedIds) {
  const naturalKey = getNaturalAdKey(placement, placementDurations, siteServedIds);
  const choices = [];

  (groups || []).forEach((group, index) => {
    if (group.naturalKey !== naturalKey) return;
    choices.push({ slot: group.slot, adNumber: index + 1 });
  });

  return choices;
}

/**
 * The lowest slot not yet used within a placement's natural group, for "New ad".
 */
export function getNextAdSlot(placement, groups, placementDurations, siteServedIds) {
  const used = new Set(
    getAdSlotChoices(placement, groups, placementDurations, siteServedIds).map(
      (c) => c.slot
    )
  );
  let slot = 1;
  while (used.has(slot)) slot += 1;
  return slot;
}
